import { fetchBookingRecords } from "../../../service/bookingService";
import { normaliseStatus, STATUS } from "../../../pages/comms/Bookings/bookingStatus";

/**
 * Client bookings a leave would hit.
 *
 * Mirrors what approving a leave cancels server side
 * (`leave_booking_effects.py`): the coach's bookings that overlap the leave,
 * start after now, and are still Confirmed, Rescheduled or No Show.
 */

const CANCELLABLE = new Set([STATUS.CONFIRMED, STATUS.RESCHEDULED, STATUS.NO_SHOW]);

/**
 * @param {Array<Object>} records - `/fetch-booking-records` rows.
 * @param {{ employee_id: string, start_date: string, end_date: string }} leave
 *   - A leave row, or the same three fields for a request not yet sent.
 * @param {Date} [now]
 * @returns {Array<Object>} The affected bookings, earliest first.
 */
export function activeBookingsInLeave(records, leave, now = new Date()) {
  const leaveStart = new Date(leave?.start_date).getTime();
  const leaveEnd = new Date(leave?.end_date).getTime();
  if (Number.isNaN(leaveStart) || Number.isNaN(leaveEnd)) return [];
  return (Array.isArray(records) ? records : [])
    .filter((b) => String(b?.coach_id) === String(leave.employee_id))
    .filter((b) => CANCELLABLE.has(normaliseStatus(b?.booking_status)))
    .filter((b) => {
      const start = new Date(b?.start_time).getTime();
      const end = new Date(b?.end_time || b?.start_time).getTime();
      return start > now.getTime() && start < leaveEnd && Math.max(end, start) > leaveStart;
    })
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
}

/**
 * Booking records starting inside a window. Never filters by `userId` —
 * somebody else's is a 403 (Bookings gotcha 9).
 *
 * @param {Function} get
 * @param {{ eventTypeId: string, employeeId?: string, from: string, to: string }} params
 *   - `employeeId` only when a manager reads one coach; the caller's own read
 *   leaves it unset (see `fetchBookingRecords`).
 * @returns {Promise<Array<Object>>}
 */
export async function loadBookings(get, { eventTypeId, employeeId, from, to }) {
  const payload = await fetchBookingRecords(get, {
    eventTypeId,
    employeeId,
    fromDate: from,
    toDate: to,
    limit: 500,
  });
  return Array.isArray(payload?.booking_records) ? payload.booking_records : [];
}
